import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mhome/ui";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import calendar from "dayjs/plugin/calendar";
import DeleteIcon from "@mui/icons-material/Delete";
import { useRecoilValue } from "recoil";
import { backgroundTypeState } from "../global-state";
import {
  getBackground,
  useComponentBackgroundColor,
} from "../common/adaptive-theme-provider";
import { safeAreaInsetsState } from "../global-state";
import { useCustomFormat } from "./after-init";

dayjs.extend(relativeTime);
dayjs.extend(calendar);

const NotificationContent = ({
  setNotification,
  combinedItems,
  onClose,
  onClearNotifications,
  onDialogClick,
  onNotificationClick,
  calculateRemainingTime,
  calculateDialogRemainingTime,
  formatCountdown,
  currentTime,
  onDelete,
}) => {
  const getComponentBgColor = useComponentBackgroundColor();
  const backgroundType = useRecoilValue(backgroundTypeState);
  const safeAreaInsets = useRecoilValue(safeAreaInsetsState);
  const customFormat = useCustomFormat();

  const safeSetNotification = useCallback(
    (item) => {
      if (typeof setNotification === "function") {
        setNotification(item);
      } else {
        console.warn("setNotification is not a valid function");
      }
    },
    [setNotification]
  );

  return (
    <div
      style={{
        width: "100%",
        height: "100vh",
        paddingTop: `${safeAreaInsets.top}px`,
        background: getBackground(backgroundType),
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "8px 16px",
          background: "transparent",
          minHeight: "56px",
          height: "56px",
          marginBottom: 0,
        }}
      >
        <IconButton
          aria-label="close"
          onClick={onClose}
          edge="start"
          style={{
            marginRight: "16px",
            padding: "8px",
            color: "hsl(var(--foreground))",
          }}
        >
          <CloseIcon style={{ fontSize: "1.375rem" }} />
        </IconButton>
        <Typography
          variant="h6"
          component="div"
          style={{
            flexGrow: 1,
            color: "hsl(var(--foreground))",
            fontSize: "1.125rem",
            lineHeight: "1.2",
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "100%",
          }}
        >
          Notifications
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={onClearNotifications}
          style={{
            textTransform: "none",
            borderRadius: "16px",
            fontSize: "0.75rem",
            padding: "4px 12px",
            minWidth: "auto",
            boxShadow: "none",
          }}
        >
          Clear All
        </Button>
      </div>
      <List
        style={{
          flex: 1,
          overflow: "auto",
          paddingLeft: "16px",
          paddingRight: "16px",
          paddingTop: "8px",
          paddingBottom: "8px",
        }}
      >
        {combinedItems.map((item, index) => {
          const remainingTime = item.isDialog
            ? calculateDialogRemainingTime(item.createdAt, item.timeout)
            : item.timeout
            ? calculateRemainingTime(item.createdAt, item.timeout)
            : null;

          // Ensure unique key for each item
          const key = item.isDialog ? `d-${item.id}` : `n-${item.id || index}`;

          return (
            <React.Fragment key={key}>
              <ListItem
                button
                style={{
                  backgroundColor: item.isPinned
                    ? getComponentBgColor("hsl(var(--accent))")
                    : item.isDialog
                    ? getComponentBgColor("hsl(var(--card))")
                    : getComponentBgColor("hsl(var(--card))"),
                  marginTop: "4px",
                  marginBottom: "4px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderRadius: "16px",
                  padding: "4px 8px",
                  border: item.isDialog
                    ? `2px solid hsl(var(--primary))`
                    : "none",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "hsl(var(--accent))";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = item.isPinned
                    ? getComponentBgColor("hsl(var(--accent))")
                    : item.isDialog
                    ? getComponentBgColor("hsl(var(--card))")
                    : getComponentBgColor("hsl(var(--card))");
                }}
              >
                <ListItemText
                  primary={
                    <div
                      onClick={() => {
                        if (item.isDialog) {
                          // For dialog items, trigger the dialog click handler
                          onDialogClick(item);
                        } else {
                          // For notification items, trigger the notification click handler
                          safeSetNotification(item);
                          onNotificationClick(item);
                        }
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "4px",
                        }}
                      >
                        <Typography
                          variant="caption"
                          style={{
                            color: "hsl(var(--muted-foreground))",
                            fontSize: "0.75rem",
                          }}
                          component="div"
                        >
                          {customFormat(
                            dayjs(
                              item.isDialog
                                ? item.createdAt * 1000
                                : item.timestamp
                            ),
                            currentTime
                          )}
                        </Typography>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          {!item.isDialog && (
                            <Typography
                              variant="subtitle1"
                              component="span"
                              style={{
                                fontSize: "0.875rem",
                                fontWeight: item.isPinned ? 600 : 500,
                                color: item.isSystem
                                  ? item.systemLevel === 3
                                    ? "hsl(var(--destructive))"
                                    : item.systemLevel === 2
                                    ? "hsl(var(--warning))"
                                    : "hsl(var(--foreground))"
                                  : "hsl(var(--foreground))",
                                display: "block",
                                marginBottom: "4px",
                              }}
                            >
                              {item.title || "Action Required"}
                            </Typography>
                          )}
                          {(item.message || item.content) && (
                            <Typography
                              variant="body2"
                              component="div"
                              style={{
                                fontSize: item.isDialog
                                  ? "0.875rem"
                                  : "0.75rem",
                                color: item.isDialog
                                  ? "hsl(var(--foreground))"
                                  : "hsl(var(--muted-foreground))",
                                fontWeight: item.isDialog ? 500 : 400,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                              }}
                            >
                              {item.message || item.content}
                            </Typography>
                          )}
                        </div>
                        {!item.isPinned &&
                          !item.isDialog &&
                          (item.isAnnounce ? (
                            <Button
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDelete(item);
                              }}
                            >
                              Confirm
                            </Button>
                          ) : (
                            <IconButton
                              aria-label="delete"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDelete(item);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          ))}
                      </div>
                    </div>
                  }
                />
                {remainingTime !== null && remainingTime > 0 && (
                  <div
                    style={{
                      position: "relative",
                      display: "inline-flex",
                    }}
                  >
                    <CircularProgress
                      variant="determinate"
                      value={(remainingTime / item.timeout) * 100}
                      size={40}
                      thickness={4}
                      style={{
                        color:
                          remainingTime <= 60
                            ? "hsl(var(--destructive))"
                            : remainingTime <= 600
                            ? "#FFD700"
                            : "hsl(var(--primary))",
                      }}
                    />
                    <div
                      style={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: "absolute",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        component="div"
                        style={{
                          color:
                            remainingTime <= 60
                              ? "hsl(var(--destructive))"
                              : remainingTime <= 600
                              ? "#FFD700"
                              : "hsl(var(--muted-foreground))",
                          fontWeight: remainingTime <= 60 ? 600 : 400,
                          fontSize: "0.7rem",
                        }}
                      >
                        {formatCountdown(remainingTime)}
                      </Typography>
                    </div>
                  </div>
                )}
              </ListItem>
            </React.Fragment>
          );
        })}
      </List>
    </div>
  );
};

const NotificationDialogList = ({
  notifications,
  setNotification,
  dialogs,
  onClose,
  onNotificationClick,
  onDialogClick,
  onDialogTimeout,
  open,
  onClearNotifications,
  onDelete,
  setNotificationCount,
}) => {
  const backgroundType = useRecoilValue(backgroundTypeState);
  const [currentTime, setCurrentTime] = useState(dayjs());

  useEffect(() => {
    const now = dayjs();

    let dd = null;
    for (let i = 0; i < dialogs.length; i++) {
      const d = dialogs[i];
      // Convert createdAt from seconds to milliseconds and timeout from seconds to seconds
      if (
        dayjs(d.createdAt * 1000)
          .add(d.timeout, "second")
          .isBefore(now)
      ) {
        dd = d;
        break;
      }
    }
    if (dd) {
      onDialogTimeout(dd);
    }
  }, [dialogs]);

  useEffect(() => {
    // Update more frequently if we have dialogs with timeouts
    const hasDialogsWithTimeout = dialogs.some(
      (d) => d.timeout && d.timeout > 0
    );
    const interval = hasDialogsWithTimeout ? 1000 : 60000; // 1 second if dialogs, 1 minute otherwise

    const intervalId = setInterval(() => {
      setCurrentTime(dayjs());
    }, interval);

    return () => clearInterval(intervalId); // Cleanup interval on unmount
  }, [dialogs]);

  const calculateRemainingTime = (timeout) => {
    const now = currentTime;
    const endTime = dayjs(timeout);
    return endTime.diff(now, "second");
  };

  const calculateDialogRemainingTime = (createdAt, timeout) => {
    const now = currentTime;
    const expiryTime = dayjs(createdAt * 1000).add(timeout, "second");
    return Math.max(0, expiryTime.diff(now, "second"));
  };

  const formatCountdown = (remainingSeconds) => {
    if (remainingSeconds <= 0) return "Expired";

    const days = Math.floor(remainingSeconds / (24 * 60 * 60));
    const hours = Math.floor((remainingSeconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((remainingSeconds % (60 * 60)) / 60);
    const seconds = remainingSeconds % 60;

    if (days > 0) {
      return `${days}d`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    } else {
      return `${seconds}s`;
    }
  };

  // Update notification count whenever notifications or dialogs change
  useEffect(() => {
    const totalCount = notifications.length + dialogs.length;
    if (setNotificationCount) {
      setNotificationCount(totalCount);
    }
  }, [notifications, dialogs, setNotificationCount]);

  // Combine and sort notifications and dialogs by priority: pinned > dialogs > notifications
  const combinedItems = [...notifications, ...dialogs].sort((a, b) => {
    // First sort by pinned status (pinned items come first)
    if (a.isPinned && !b.isPinned) return -1;
    if (!a.isPinned && b.isPinned) return 1;

    // If both are pinned or both are not pinned, continue with priority sorting
    // Second sort by dialog status (dialogs come before notifications)
    if (a.isDialog && !b.isDialog) return -1;
    if (!a.isDialog && b.isDialog) return 1;

    // Then sort by timestamp within the same type
    const t1 = a.isDialog ? a.createdAt : a.timestamp;
    const t2 = b.isDialog ? b.createdAt : b.timestamp;
    return t2 - t1;
  });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // Add explicit transition duration and keep mounted for better animation
      transitionDuration={300}
      keepMounted={true}
      style={{
        zIndex: "var(--z-index-drawer)",
      }}
      PaperProps={{
        style: {
          borderRadius: "16px 0 0 16px",
          width: "min(100vw, 400px)",
          boxShadow: "none",
          transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important",
        },
      }}
      BackdropProps={{
        style: {
          transition: "opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important",
        },
      }}
    >
      <NotificationContent
        setNotification={setNotification}
        combinedItems={combinedItems}
        onClose={onClose}
        onClearNotifications={onClearNotifications}
        onDialogClick={onDialogClick}
        onNotificationClick={onNotificationClick}
        calculateRemainingTime={calculateRemainingTime}
        calculateDialogRemainingTime={calculateDialogRemainingTime}
        formatCountdown={formatCountdown}
        currentTime={currentTime}
        onDelete={onDelete}
      />
    </Drawer>
  );
};

export default NotificationDialogList;
